import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '~app/core/auth/store';
import { apiClient } from '~app/core/api-client';
import { isTransactionCreated } from '~app/core/auth/client/transaction';
import router from '~app/app.routes';
import { createError, errorRoute } from '~app/core/error';

export const tokenHeaderInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const { accessToken, tokenType } = useAuthStore().token;

  if (accessToken && tokenType) {
    config.headers.Authorization = `${tokenType} ${accessToken}`;
  }

  return config;
};

export const authErrorHandlerInterceptor = (error: AxiosError): Promise<any> => {
  const authStore = useAuthStore();

  switch (error?.response?.status) {
    case 400:
      //bad request
      break;
    case 401:
      //token expired or not authenticated
      return authStore.isAccessTokenExpired
        ? _tokenExpiredHandler(error)
        : _notAuthenticatedHandler(error);
    case 403:
      //not authorized - forbidden
      return _notAuthorizedHandler(error);
    case 419:
    case 440:
      //session timeout
      break;
    case 498:
      //token expired
      return _tokenExpiredHandler(error);
  }

  return Promise.reject(error);
};

function _notAuthenticatedHandler(error: AxiosError): Promise<void> {
  const authStore = useAuthStore();

  if (isTransactionCreated()) {
    return _redirectToErrorPage(error);
  }

  return authStore.loginWithRedirect();
}

function _notAuthorizedHandler(error: AxiosError): Promise<any> {
  const authStore = useAuthStore();

  if (isTransactionCreated()) {
    return _redirectToErrorPage(error);
  }

  if (!authStore.isAuthenticated) {
    return authStore.loginWithRedirect();
  }

  return Promise.reject(error);
}

function _redirectToErrorPage(error: AxiosError) {
  const { name, message, code } = createError(error);
  router.replace({ name: errorRoute.name, params: { name, message, code } });
  return Promise.reject(error);
}

function _tokenExpiredHandler(error: AxiosError): Promise<any> {
  const authStore = useAuthStore();

  return authStore
    .refreshOAuthToken()
    .then(authStore.fetchUser)
    .catch(_notAuthenticatedHandler)
    .then(() => apiClient.request(error.config));
}
